/**
 * objectStorageService（SEC-01 S3 落地）
 *
 * 本模块是**纯 R2 PUT 传输**实现：浏览器不再持有任何 R2/S3 长期凭证，
 * 也不与本模块外的 Backend / Supabase / presign 端点交互。调用方（S4 批次
 * 上传服务、S7 `/photos/` 复用服务）必须已经持有后端签发的预签名 PUT URL
 * 与 required_headers，才能调用本模块直传 R2。
 *
 * 职责边界（方案 §5.1 / §4.4）：
 * - 规范化上传体（File / Blob / data URL → body）
 * - 以 `fetch` PUT 到 `uploadUrl`，并原样发送 `requiredHeaders` 全部 entries
 * - 仅 HTTP `200` 视为成功确认；任意其他 HTTP 状态 / CORS / 网络失败均失败
 * - 不读取 Supabase session、不调用 Backend、不生成 key、不做重签
 *
 * 安全约束（方案 §4.4 / §6.3）：
 * - `uploadUrl` 是 bearer 级凭证：不写入日志 / 埋点 / 错误上报 / 持久缓存
 * - 错误对象不含 `upload_url`、R2 XML body 或原始响应体（fail-closed）
 * - 不向 R2 发送 Backend Bearer token / Cookie / 业务请求头；只发 required_headers
 */

const createPresignedUploadError = (code, status = null) => {
    const error = new Error(`[objectStorageService] ${code}`);
    error.code = code;
    error.status = status;
    error.responseData = null;
    return error;
};

const convertDataUrlToBlob = (value) => {
    const match = /^data:([^;,]*)(;base64)?,(.*)$/s.exec(String(value || ''));
    if (!match) {
        throw createPresignedUploadError('invalid_upload_source');
    }

    const mime = match[1] || 'application/octet-stream';
    const isBase64 = Boolean(match[2]);
    const data = match[3];

    if (!data) {
        throw createPresignedUploadError('invalid_upload_source');
    }

    let bytes;
    if (isBase64) {
        let binary;
        try {
            binary = atob(data);
        } catch (decodeError) {
            // atob 对非法 base64 抛出无 code 的原生异常；规范化为稳定的确定性错误码，
            // 避免上层 runAssetUploadService 的 catch 把它误判为 R2 未确认（R2 unconfirmed）。
            throw createPresignedUploadError('invalid_upload_source');
        }
        bytes = new Uint8Array(binary.length);
        for (let index = 0; index < binary.length; index += 1) {
            bytes[index] = binary.charCodeAt(index);
        }
    } else {
        let decoded;
        try {
            decoded = decodeURIComponent(data);
        } catch (decodeError) {
            // decodeURIComponent 对非法百分号序列同样抛出无 code 的原生异常；同样规范化为稳定错误码。
            throw createPresignedUploadError('invalid_upload_source');
        }
        bytes = new Uint8Array(decoded.length);
        for (let index = 0; index < decoded.length; index += 1) {
            bytes[index] = decoded.charCodeAt(index);
        }
    }

    return new Blob([bytes], { type: mime });
};

const normalizeUploadBody = (source) => {
    if (source instanceof File || source instanceof Blob) {
        return source;
    }
    if (typeof source === 'string' && source.startsWith('data:')) {
        return convertDataUrlToBlob(source);
    }
    throw createPresignedUploadError('invalid_upload_source');
};

// 原样复制 required_headers 全部 entries；不增删、不覆盖、不依赖本地推导值。
// 每项 value 必须是 string（已由 Gateway normalizer 保证）；遇到非 string 直接失败，
// 不再静默跳过或 String() 转换（方案 §5.4 / A.2 P2）。
const buildRequestHeaders = (requiredHeaders = {}) => {
    const headers = {};
    if (requiredHeaders && typeof requiredHeaders === 'object' && !Array.isArray(requiredHeaders)) {
        Object.keys(requiredHeaders).forEach((key) => {
            const rawValue = requiredHeaders[key];
            if (typeof rawValue !== 'string') {
                throw createPresignedUploadError('presigned_upload_invalid_header');
            }
            headers[String(key)] = rawValue;
        });
    }
    return headers;
};

// 大图直传给宽限：移动端弱网下 TCP 静默断连（无 RST）时，无 abort 的 fetch 会
// 永远 pending（生产案例：execution_started 后整个执行链静默挂死、runs 永不提交）。
// 超时按 transport 失败归类，runAssetUploadService 会将其视为可重授权重试的错误码。
const PRESIGNED_PUT_TIMEOUT_MS = 120000;

/**
 * 使用后端签发的预签名 PUT URL 直传 R2。
 *
 * @param {Object} params
 * @param {Blob|File|string} params.source      - 上传体：File / Blob 或 data URL 字符串
 * @param {string} params.uploadUrl             - 后端签发的预签名 PUT URL（bearer 凭证）
 * @param {Object<string,string>} [params.requiredHeaders] - 后端要求显式发送的全部 HTTP 头
 * @returns {Promise<{ status: number }>} 仅在 HTTP 200 时 resolve
 */
export const putPresignedObject = async ({ source, uploadUrl, requiredHeaders = {} } = {}) => {
    if (!uploadUrl || typeof uploadUrl !== 'string' || uploadUrl.trim() === '') {
        throw createPresignedUploadError('presigned_upload_missing_url');
    }

    const body = normalizeUploadBody(source);
    const headers = buildRequestHeaders(requiredHeaders);

    const abortController = new AbortController();
    const abortTimer = setTimeout(() => {
        abortController.abort();
    }, PRESIGNED_PUT_TIMEOUT_MS);

    let response;
    try {
        response = await fetch(uploadUrl, {
            method: 'PUT',
            headers,
            body,
            signal: abortController.signal
        });
    } catch (transportError) {
        // CORS / network failure / 超时中止：结果不可确认；fail-closed，错误不带 URL/body。
        throw createPresignedUploadError('presigned_upload_transport_failed', null);
    } finally {
        clearTimeout(abortTimer);
    }

    // 仅 HTTP 200 是稳定成功条件；201/204 等其他 2xx 不视为契约成功。
    if (response.status !== 200) {
        throw createPresignedUploadError('presigned_upload_failed', response.status);
    }

    return { status: response.status };
};

export const objectStorageService = {
    putPresignedObject
};

export default objectStorageService;
